import * as React from 'react';
import { observer } from 'mobx-react';

import { SvgIcon } from '../../components/svg-icon';
import logo from '../../assets/svg/pushpay-logo-filled.svg';
import { WebGivingPushpayInfo, WebGivingMerchant } from '../../webgiving-generated';
import { WebGivingUserAction } from '../../webgiving-user-actions';
import { ModalType } from '../../modals/modal-type';
import { classNames } from '../../../Shared/utils/classnames';
import { injectWebGivingProps, InjectableWebGivingProps, raiseAction } from '../../utils/injectable-props';
import { getIconScale, getSupportedCardIconParams } from '../../utils/card-utils';
import { TermsAndConditionsFooter } from '../../components/terms-and-conditions';
import { SupportedCardBrands } from '../../components/supported-card-brands/supported-card-brands';
import { Text } from '../../components/text';

import {
	footer,
	footerContainer,
	footerLink,
	orgInfo,
	separator,
	pushpayLogoWrapper,
	pushpayLogo,
	info,
	brands,
	brandsTitle,
	brandsWrapper,
	footerDark,
	wrapper
} from './footer.less';

export interface IFooterProp {
	merchant: WebGivingMerchant;
	className?: string;
	pushpayInfo: WebGivingPushpayInfo;
	showSupportedPaymentMethods?: boolean;
	showPayerCoverFeesLegalText: boolean;
	hasBackdrop?: boolean;
}

@injectWebGivingProps
@observer
export class WebGivingMainFooter extends React.Component<IFooterProp & InjectableWebGivingProps> {
	render() {
		const { hasBackdrop, merchant, pushpayInfo, className, showPayerCoverFeesLegalText, showSupportedPaymentMethods } =
			this.props;
		const { HelpCenterLink: helpCenterLink, TermsAndConditionsUrl, PrivacyStatementUrl } = pushpayInfo;
		const {
			LegalName: legalName,
			Address: address,
			TradingName: tradingName,
			HasCustomTermsOrPrivacy: hasCustomLinks,
			SupportedCardBrandKeys,
			SupportsApplePay,
			SupportsAch,
		} = merchant;
		return (
			// tslint:disable:max-line-length
			<FooterContainer className={classNames( footer, hasBackdrop && footerDark, className)}>
				{showSupportedPaymentMethods && (
					<FooterSupportedPaymentMethods
						SupportedCardBrandKeys={SupportedCardBrandKeys}
						SupportsApplePay={SupportsApplePay}
						SupportsAch={SupportsAch}
					/>
				)}
				<TermsAndConditionsFooter tradingName={tradingName} hasCustomLinks={hasCustomLinks}  privacyStatementUrl={PrivacyStatementUrl} termsAndConditionsUrl={TermsAndConditionsUrl}/>
				{showPayerCoverFeesLegalText && (
					<p>
						<Text>
							*This rate represents an approximation of the applicable processing rates; actual processing
							costs may be higher or lower.
						</Text>
					</p>
				)}
				{(legalName || address) && (
					<div className={orgInfo}>
						<p>
							{legalName && (
								<span>
									<strong>
										<Text>Organization Legal Name</Text>:
									</strong> {legalName}
								</span>
							)}
							{legalName && address && <span className={separator}>|</span>}
							{address && (
								<span>
									<Text>Address</Text>: {address}
								</span>
							)}
						</p>
					</div>
				)}
				<FooterPushpaySection helpCenterLink={helpCenterLink} />
			</FooterContainer>
			// tslint:enable:max-line-length
		);
	}

	openPushpayTerms = () => {
		raiseAction(new WebGivingUserAction.OpenModalDialog(ModalType.PushpayTerms), this.props);
	};
}

@observer
export class FooterContainer extends React.Component<{ className?: string }, {}> {
	render() {
		const { className, children } = this.props;
		return (
			<footer className={className}>
				<div className={wrapper}>
					<div className={classNames(footerContainer, 'container')}>{children}</div>
				</div>
			</footer>
		);
	}
}

export const FooterPushpaySection = observer(({ helpCenterLink }: { helpCenterLink: string }) => {
	return (
		<div className={info}>
			<div className={pushpayLogoWrapper}>
				<SvgIcon className={pushpayLogo} svg={logo} />
				<a
					className={footerLink}
					href={helpCenterLink}
					target="_blank"
					rel="noopener noreferrer"
				>
					<strong><Text>Help Center</Text></strong>
				</a>
				<span className={separator}>|</span>
			</div>
			<div>
				<strong>&copy; {new Date().getFullYear()} Pushpay<sup>&reg;</sup> <Text>Ltd, All rights reserved</Text></strong>
			</div>
		</div>
	);
});

export const FooterSupportedPaymentMethods: React.FC<{
	SupportedCardBrandKeys: string[];
	SupportsApplePay?: boolean;
	SupportsAch?: boolean;
}> = ({ SupportedCardBrandKeys, SupportsApplePay, SupportsAch }) => {
	const supportedCardStyling = getSupportedCardIconParams();

	return (
		<div className={brands}>
			<span className={brandsTitle}>
				<Text>Accepted payment methods</Text>
			</span>
			<SupportedCardBrands
				brandKeys={SupportedCardBrandKeys}
				iconParams={{
					...supportedCardStyling,
					...(SupportsApplePay ? { applepay: { scale: getIconScale('applepay', true) } } : {}),
				}}
				wrapperClassName={brandsWrapper}
				applePay={SupportsApplePay}
				ach={SupportsAch}
			/>
		</div>
	);
};
